import React, { useState } from 'react';
import { Layout, Menu, Button, Dropdown, Tooltip, Grid, Drawer} from 'antd';
import {
    SettingOutlined,
    MenuOutlined,
    LogoutOutlined,
    PhoneOutlined,
    DashboardOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UserOutlined,
    DownOutlined,
    TeamOutlined,
    CodeOutlined,
    BookOutlined,
    QuestionCircleOutlined,
    StarOutlined,
    FileProtectOutlined,
    SunOutlined,
    MoonOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/admin/index.css';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const pathMap = {
    '/admin': 'Genel Bakış',
    '/admin/leads': 'Aranacaklar',
    '/admin/chat-options': 'Chat Seçenekleri',
    '/admin/prompts': 'LLM Promptları',
    '/admin/knowledge-base': 'Bilgi Kazanı',
    '/admin/unanswered-questions': 'Cevaplanamayan Sorular',
    '/admin/feedback': 'Geri Bildirimler',
    '/admin/agents': 'Temsilciler',
    '/admin/consent-records': 'Onay Kayıtları',
    '/admin/settings': 'Ayarlar',
};

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { agent, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [collapsed, setCollapsed] = useState(false);
    // Eshikler antd'nin token'lari — CSS ile ayni degerleri kullaniyoruz ki
    // layout bir yerde, tablolar baska yerde kirilmasin.
    const screens = useBreakpoint();
    const isMobile = !screens.lg; // < 992px
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const allMenuItems = [
        { key: '/admin', icon: <DashboardOutlined />, label: 'Genel Bakış', roles: ['ADMIN', 'CONTENT_ADMIN'] },
        { key: '/admin/leads', icon: <PhoneOutlined />, label: 'Aranacaklar', roles: ['ADMIN', 'CALL_CENTER'] },
        { key: '/admin/chat-options', icon: <MenuOutlined />, label: 'Chat Seçenekleri', roles: ['ADMIN'] },
        { key: '/admin/prompts', icon: <CodeOutlined />, label: 'LLM Promptları', roles: ['ADMIN'] },
        { key: '/admin/knowledge-base', icon: <BookOutlined />, label: 'Bilgi Kazanı', roles: ['ADMIN', 'CONTENT_ADMIN'] },
        { key: '/admin/unanswered-questions', icon: <QuestionCircleOutlined />, label: 'Cevaplanamayan Sorular', roles: ['ADMIN', 'CONTENT_ADMIN'] },
        { key: '/admin/feedback', icon: <StarOutlined />, label: 'Geri Bildirimler', roles: ['ADMIN'] },
        { key: '/admin/agents', icon: <TeamOutlined />, label: 'Temsilciler', roles: ['ADMIN'] },
        { key: '/admin/consent-records', icon: <FileProtectOutlined />, label: 'Onay Kayıtları', roles: ['ADMIN'] },
        { key: '/admin/settings', icon: <SettingOutlined />, label: 'Ayarlar', roles: ['ADMIN'] },
    ];

    const menuItems = allMenuItems
        .filter(item => item.roles.includes(agent?.role))
        .map(({ roles, ...item }) => item);

    const handleMenuClick = ({ key }) => {
        navigate(key);
        setMobileMenuOpen(false);
    };

    const userMenuItems = [
        { key: 'profile', icon: <UserOutlined />, label: 'Profil' },
        { key: 'logout', icon: <LogoutOutlined />, label: 'Çıkış Yap', danger: true, onClick: handleLogout },
    ];

    const getInitials = (name) => {
        if (!name) return 'A';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const pageVariants = {
        initial: { opacity: 0, y: 16 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -16 },
    };

    const currentPage = pathMap[location.pathname] || 'Admin Panel';

    return (
        <Layout className="admin-layout" style={{ minHeight: '100vh' }}>
            {/* Sidebar */}
            <Sider
                width={240}
                collapsedWidth={isMobile ? 0 : 64}
                collapsed={isMobile ? true : collapsed}
                breakpoint="lg"
                trigger={null}
                className="admin-sidebar"
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 100,
                }}
            >
                {/* Logo */}
                <motion.div
                    className="admin-sidebar-logo"
                    initial={false}
                    animate={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
                >
                    <div className="admin-sidebar-logo-icon">
                        L
                    </div>
                    <AnimatePresence>
                        {!collapsed && (
                            <motion.div
                                className="admin-sidebar-logo-text"
                                initial={{ opacity: 0, width: 0 }}
                                animate={{ opacity: 1, width: 'auto' }}
                                exit={{ opacity: 0, width: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                <span>Lidya</span> Admin
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Menu */}
                <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    onClick={handleMenuClick}
                />

                {/* Sidebar Footer */}
                <div className="admin-sidebar-footer">
                    <Tooltip title={collapsed ? (theme === 'light' ? 'Karanlık Tema' : 'Aydınlık Tema') : ''} placement="right">
                        <button
                            className="admin-sidebar-theme-toggle"
                            onClick={toggleTheme}
                        >
                            {theme === 'light' ? <MoonOutlined /> : <SunOutlined />}
                            {!collapsed && (theme === 'light' ? ' Karanlık Tema' : ' Aydınlık Tema')}
                        </button>
                    </Tooltip>
                    <Button
                        className="admin-sidebar-collapse-btn"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {!collapsed && 'Daralt'}
                    </Button>
                </div>
            </Sider>

            {/* Mobil menu — Sider 992px altinda gizlendigi icin menuye
                erisim buradan saglanir */}
            <Drawer
                placement="left"
                open={isMobile && mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                width={260}
                closable={false}
                className="admin-mobile-menu"
                rootClassName="admin-mobile-menu"
                styles={{ body: { padding: 0 } }}
            >
                <div className="admin-sidebar-logo">
                    <div className="admin-sidebar-logo-icon">L</div>
                    <div className="admin-sidebar-logo-text">
                        <span>Lidya</span> Admin
                    </div>
                </div>
                <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    onClick={handleMenuClick}
                />
            </Drawer>

            {/* Main Layout */}
            <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? 64 : 240), transition: 'margin-left 0.2s' }}>
                {/* Header */}
                <Header className="admin-header">
                    <div className="admin-header-left">
                        {isMobile && (
                            <Button
                                className="admin-header-menu-btn"
                                icon={<MenuUnfoldOutlined />}
                                onClick={() => setMobileMenuOpen(true)}
                                aria-label="Menüyü aç"
                            />
                        )}
                        <div className="admin-header-breadcrumb">
                            <span className="admin-header-breadcrumb-root">Admin</span>
                            {location.pathname !== '/admin' && (
                                <>
                                    <RightOutlined className="admin-header-breadcrumb-separator" />
                                    <span className="admin-header-breadcrumb-current">
                                        {currentPage}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="admin-header-right">
                        {/* User Dropdown */}
                        <Dropdown
                            menu={{ items: userMenuItems }}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <motion.div
                                className="admin-header-user"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <div className="admin-header-user-avatar">
                                    {getInitials(agent?.displayName)}
                                </div>
                                <div className="admin-header-user-info">
                                    <span className="admin-header-user-name">
                                        {agent?.displayName || 'Admin'}
                                    </span>
                                    <span className="admin-header-user-role">
                                        {agent?.role === 'ADMIN' ? 'Yönetici' : agent?.role === 'CONTENT_ADMIN' ? 'İçerik Yöneticisi' : agent?.role === 'CALL_CENTER' ? 'Çağrı Merkezi' : '-'}
                                    </span>
                                </div>
                                <DownOutlined style={{ fontSize: 10, color: 'var(--admin-text-secondary)' }} />
                            </motion.div>
                        </Dropdown>
                    </div>
                </Header>

                {/* Content */}
                <Content className="admin-content">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            variants={pageVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.25 }}
                        >
                            <Outlet />
                        </motion.div>
                    </AnimatePresence>
                </Content>
            </Layout>

        </Layout>
    );
};

export default AdminLayout;
